import React, { useEffect, useMemo, useState, useRef } from "react";
import { MapPin, ChevronRight } from "lucide-react";
import { catalogApi, adminApi, toApiAbsoluteUrl } from "../../api/client";
import {
  FormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  LinearProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { useLocation, useNavigate, Link } from "react-router-dom";

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [filters, setFilters] = useState({
    q: "",
    categoryIds: [],
    subcategoryIds: [],
    tags: "",
    priceMin: "",
    priceMax: "",
    lat: "",
    lng: "",
    sort: "created_desc",
    page: 1,
    limit: 20,
    providerUserId: "",
  });
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [providers, setProviders] = useState([]);
  const [firstSearchDone, setFirstSearchDone] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isInitializedRef = useRef(false);

  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    listing: null,
  });
  const [editForm, setEditForm] = useState({
    name: "",
    price: "",
    description: "",
    phoneNumber: "",
    address: "",
    maxRadiusKm: "",
    tags: [],
  });
  const [editLoading, setEditLoading] = useState(false);

  // View modal state
  const [viewModal, setViewModal] = useState({
    open: false,
    listing: null,
  });

  const urlProviderId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("providerUserId") || "";
  }, [location.search]);

  const currencyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const cat = await catalogApi.listCategories();
        setCategories(cat?.items || []);
        try {
          const res = await adminApi.listUsers({
            role: "provider",
            includeProfile: true,
            page: 1,
            limit: 500,
          });
          const opts = (res?.items || []).map((p) => ({
            id: p.id,
            label: p?.providerProfile?.companyName || p.email,
          }));
          setProviders(opts);
        } catch {
          setProviders([]);
        }
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    if (urlProviderId) {
      setFilters((p) => ({ ...p, providerUserId: urlProviderId, page: 1 }));
      runSearch({ page: 1, providerUserId: urlProviderId }).finally(() => {
        isInitializedRef.current = true;
        setFirstSearchDone(true);
      });
    } else {
      runSearch({ page: 1 }).finally(() => {
        isInitializedRef.current = true;
        setFirstSearchDone(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProviderId]);

  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (urlProviderId && filters.providerUserId !== urlProviderId) return;
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.q,
    filters.categoryIds,
    filters.subcategoryIds,
    filters.tags,
    filters.priceMin,
    filters.priceMax,
    filters.sort,
    filters.page,
    filters.limit,
    filters.providerUserId,
  ]);

  const handleCategoriesChange = async (event) => {
    const value = event.target.value;
    const selected = typeof value === "string" ? value.split(",") : value;
    setFilters((p) => ({ ...p, categoryIds: selected, subcategoryIds: [] }));
    if (selected.length > 0) {
      try {
        const all = await Promise.all(
          selected.map((id) => catalogApi.listSubcategories(id))
        );
        const merged = (all || []).flatMap((r) => r?.items || []);
        const seen = new Set();
        const unique = [];
        for (const s of merged) {
          if (!seen.has(s._id)) {
            seen.add(s._id);
            unique.push(s);
          }
        }
        setSubcategories(unique);
      } catch {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  };

  const handleSubcategoriesChange = (event) => {
    const value = event.target.value;
    const selected = typeof value === "string" ? value.split(",") : value;
    setFilters((p) => ({ ...p, subcategoryIds: selected }));
  };

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };
    const params = {};
    if (merged.q) params.q = merged.q;
    if (Array.isArray(merged.categoryIds) && merged.categoryIds.length > 0)
      params.categoryIds = merged.categoryIds.join(",");
    if (
      Array.isArray(merged.subcategoryIds) &&
      merged.subcategoryIds.length > 0
    )
      params.subcategoryIds = merged.subcategoryIds.join(",");
    if (merged.tags) params.tags = merged.tags;
    if (merged.priceMin !== "" && merged.priceMin !== null)
      params.priceMin = Number(merged.priceMin);
    if (merged.priceMax !== "" && merged.priceMax !== null)
      params.priceMax = Number(merged.priceMax);
    if (merged.lat !== "" && merged.lng !== "") {
      params.lat = Number(merged.lat);
      params.lng = Number(merged.lng);
    }
    if (merged.page) params.page = Number(merged.page);
    if (merged.limit) params.limit = Number(merged.limit);
    if (merged.sort) params.sort = merged.sort;
    if (merged.providerUserId) params.providerUserId = merged.providerUserId;
    try {
      setLoading(true);
      const res = await catalogApi.publicSearch(params);
      setListings(res?.items || []);
      setListingsTotal(res?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      setListings([]);
      setListingsTotal(0);
      const msg = _e?.data?.message || "Failed to load listings";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  // Edit modal functions
  const openEditModal = (listing) => {
    setEditModal({ open: true, listing });
    setEditForm({
      name: listing?.name || "",
      price: listing?.price || "",
      description: listing?.description || "",
      phoneNumber: listing?.phoneNumber || "",
      address: listing?.address || "",
      maxRadiusKm: listing?.maxRadiusKm || "",
      tags: listing?.tags || [],
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, listing: null });
    setEditForm({
      name: "",
      price: "",
      description: "",
      phoneNumber: "",
      address: "",
      maxRadiusKm: "",
      tags: [],
    });
  };

  const handleEditSubmit = async () => {
    if (!editModal.listing) return;

    setEditLoading(true);
    try {
      await adminApi.updateListing(editModal.listing.id, editForm);

      setSnackbar({
        open: true,
        message: "Listing updated successfully",
        severity: "success",
      });

      closeEditModal();
      await runSearch();
    } catch (error) {
      console.error("Update listing error:", error);
      setSnackbar({
        open: true,
        message: error?.data?.message || "Failed to update listing",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // View modal functions
  const openViewModal = (listing) => {
    setViewModal({ open: true, listing });
  };

  const closeViewModal = () => {
    setViewModal({ open: false, listing: null });
  };

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">Listings</span>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded p-4 mb-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search keywords (q)"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Autocomplete
          size="small"
          options={providers}
          getOptionLabel={(o) => o?.label || ""}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(_e, val) =>
            setFilters((p) => ({
              ...p,
              providerUserId: val?.id || "",
              page: 1,
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Company"
              placeholder="Type company name"
            />
          )}
          value={providers.find((p) => p.id === filters.providerUserId) || null}
        />
        <FormControl size="small">
          <InputLabel id="admin-listings-cat-label">Categories</InputLabel>
          <MUISelect
            labelId="admin-listings-cat-label"
            multiple
            value={filters.categoryIds}
            onChange={handleCategoriesChange}
            input={<OutlinedInput label="Categories" />}
            renderValue={(selected) =>
              (selected || [])
                .map((id) => categories.find((c) => c._id === id)?.name || id)
                .join(", ")
            }
          >
            {(categories || []).map((c) => (
              <MenuItem key={c._id} value={c._id}>
                <Checkbox checked={filters.categoryIds.indexOf(c._id) > -1} />
                <ListItemText primary={c.name} />
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>
        <FormControl
          size="small"
          disabled={!filters.categoryIds || filters.categoryIds.length === 0}
        >
          <InputLabel id="admin-listings-subcat-label">
            Subcategories
          </InputLabel>
          <MUISelect
            labelId="admin-listings-subcat-label"
            multiple
            value={filters.subcategoryIds}
            onChange={handleSubcategoriesChange}
            input={<OutlinedInput label="Subcategories" />}
            renderValue={(selected) =>
              (selected || [])
                .map(
                  (id) => subcategories.find((s) => s._id === id)?.name || id
                )
                .join(", ")
            }
          >
            {subcategories.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                <Checkbox
                  checked={filters.subcategoryIds.indexOf(s._id) > -1}
                />
                <ListItemText primary={s.name} />
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>
        <input
          type="number"
          placeholder="Min price"
          className="border p-2 rounded"
          value={filters.priceMin}
          onChange={(e) =>
            setFilters((p) => ({ ...p, priceMin: e.target.value }))
          }
        />
        <input
          type="number"
          placeholder="Max price"
          className="border p-2 rounded"
          value={filters.priceMax}
          onChange={(e) =>
            setFilters((p) => ({ ...p, priceMax: e.target.value }))
          }
        />
        <select
          className="border p-2 rounded"
          value={filters.sort}
          onChange={(e) => setFilters((p) => ({ ...p, sort: e.target.value }))}
        >
          <option value="created_desc">Newest</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
        </select>
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSubcategories([]);
              setSearchText("");
              setFirstSearchDone(false);
              setListings([]);
              setListingsTotal(0);
              setFilters({
                q: "",
                categoryIds: [],
                subcategoryIds: [],
                tags: "",
                priceMin: "",
                priceMax: "",
                lat: "",
                lng: "",
                sort: "created_desc",
                page: 1,
                limit: filters.limit,
                providerUserId: "",
              });
              navigate({ pathname: location.pathname }, { replace: true });
              runSearch({ page: 1 }).finally(() => setFirstSearchDone(true));
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {!firstSearchDone ? (
        <div className="bg-white shadow rounded p-6 text-center text-gray-500">
          Loading...
        </div>
      ) : (
        <div className="bg-white shadow rounded">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-center">
              <thead>
                <tr>
                  <th className="p-2 border">Image</th>
                  <th className="p-2 border">Name</th>
                  <th className="p-2 border">Price</th>
                  <th className="p-2 border">Provider</th>
                  <th className="p-2 border">Category</th>
                  <th className="p-2 border">Subcategory</th>
                  <th className="p-2 border">Address</th>
                  <th className="p-2 border">Radius (km)</th>
                  <th className="p-2 border">Location</th>
                  <th className="p-2 border">Created</th>
                  <th className="p-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((it) => {
                  const imageUrls = Array.isArray(it?.images)
                    ? it.images
                        .map((img) =>
                          typeof img === "string" ? img : img?.url
                        )
                        .filter(Boolean)
                    : [];
                  const firstImage =
                    imageUrls.length > 0
                      ? toApiAbsoluteUrl(imageUrls[0])
                      : null;
                  const placeholder = `https://placehold.co/80x60/png?text=${encodeURIComponent(
                    (it?.name || "No Image").slice(0, 10)
                  )}`;
                  const providerDisplay =
                    it?.provider?.providerProfile?.companyName ||
                    it?.provider?.email ||
                    "-";
                  const categoryDisplay =
                    it?.category?.name ||
                    (it &&
                    typeof it.categoryId === "object" &&
                    it.categoryId !== null
                      ? it.categoryId.name || it.categoryId._id
                      : it?.categoryId) ||
                    "-";
                  const subcategoryDisplay =
                    it?.subcategory?.name ||
                    (it &&
                    typeof it.subcategoryId === "object" &&
                    it.subcategoryId !== null
                      ? it.subcategoryId.name || it.subcategoryId._id
                      : it?.subcategoryId) ||
                    "-";
                  return (
                    <tr key={it.id}>
                      <td className="p-2 border">
                        <img
                          src={firstImage || placeholder}
                          alt={it.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = placeholder;
                          }}
                          className="w-14 h-10 object-cover rounded"
                        />
                      </td>
                      <td className="p-2 border">{it.name}</td>
                      <td className="p-2 border tabular-nums font-mono tracking-tight">
                        {typeof it.price === "number"
                          ? currencyFmt.format(it.price)
                          : "-"}
                      </td>
                      <td className="p-2 border">{providerDisplay}</td>
                      <td className="p-2 border">
                        <Chip
                          label={categoryDisplay || "-"}
                          size="small"
                          variant="outlined"
                        />
                      </td>
                      <td className="p-2 border">
                        <Chip
                          label={subcategoryDisplay || "-"}
                          size="small"
                          variant="outlined"
                        />
                      </td>
                      <td className="p-2 border">{it.address || "-"}</td>
                      <td className="p-2 border">{it.maxRadiusKm ?? "-"}</td>
                      <td className="p-2 border">
                        {Array.isArray(it?.location?.coordinates)
                          ? (() => {
                              const lng = it.location.coordinates[0];
                              const lat = it.location.coordinates[1];
                              const url = `https://www.google.com/maps?q=${lat},${lng}`;
                              return (
                                <button
                                  onClick={() =>
                                    window.open(url, "_blank", "noopener")
                                  }
                                  className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                  title={`Open in Maps (${lat}, ${lng})`}
                                >
                                  <MapPin size={18} />
                                </button>
                              );
                            })()
                          : "-"}
                      </td>
                      <td className="p-2 border text-sm">
                        {formatDate(it?.createdAt)}
                      </td>
                      <td className="p-2 border">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openViewModal(it)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => openEditModal(it)}
                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {listings.length === 0 && (
                  <tr>
                    <td className="p-6 text-gray-500 text-center" colSpan={11}>
                      No listings
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-3 flex-wrap gap-3">
            <div className="text-sm text-gray-600">Total: {listingsTotal}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                disabled={filters.page <= 1}
                onClick={() =>
                  runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
                }
              >
                Prev
              </button>
              <span className="text-sm">Page {filters.page}</span>
              <button
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                disabled={listings.length < filters.limit}
                onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
              >
                Next
              </button>
              <select
                className="ml-2 border p-1 rounded text-sm"
                value={filters.limit}
                onChange={(e) =>
                  runSearch({ limit: Number(e.target.value), page: 1 })
                }
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-2">
          <LinearProgress />
        </div>
      )}

      {/* View Modal */}
      {viewModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Listing Details</h3>
              <button
                onClick={closeViewModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            {viewModal.listing && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Name
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.name || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Price
                      </label>
                      <p className="text-gray-900 font-mono">
                        {typeof viewModal.listing.price === "number"
                          ? currencyFmt.format(viewModal.listing.price)
                          : "-"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Description
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.description || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Phone Number
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.phoneNumber || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Service Radius
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.maxRadiusKm
                          ? `${viewModal.listing.maxRadiusKm} km`
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Category Information */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">
                    Category Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Category
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.category?.name ||
                          (viewModal.listing.categoryId &&
                          typeof viewModal.listing.categoryId === "object"
                            ? viewModal.listing.categoryId.name ||
                              viewModal.listing.categoryId._id
                            : viewModal.listing.categoryId) ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Subcategory
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.subcategory?.name ||
                          (viewModal.listing.subcategoryId &&
                          typeof viewModal.listing.subcategoryId === "object"
                            ? viewModal.listing.subcategoryId.name ||
                              viewModal.listing.subcategoryId._id
                            : viewModal.listing.subcategoryId) ||
                          "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">
                    Location Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Address
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.address || "-"}
                      </p>
                    </div>
                    {Array.isArray(
                      viewModal.listing?.location?.coordinates
                    ) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Coordinates
                        </label>
                        <div className="flex items-center gap-2">
                          <p className="text-gray-900 font-mono">
                            {viewModal.listing.location.coordinates[1]},{" "}
                            {viewModal.listing.location.coordinates[0]}
                          </p>
                          <button
                            onClick={() => {
                              const lng =
                                viewModal.listing.location.coordinates[0];
                              const lat =
                                viewModal.listing.location.coordinates[1];
                              const url = `https://www.google.com/maps?q=${lat},${lng}`;
                              window.open(url, "_blank", "noopener");
                            }}
                            className="inline-flex items-center text-blue-600 hover:text-blue-800"
                            title="Open in Maps"
                          >
                            <MapPin size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Provider Information */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">
                    Provider Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Provider
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.provider?.providerProfile
                          ?.companyName ||
                          viewModal.listing.provider?.email ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Provider Email
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.provider?.email || "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {viewModal.listing.tags &&
                  viewModal.listing.tags.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-gray-800">
                        Tags
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {viewModal.listing.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                            className="text-xs"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                {/* Images */}
                {viewModal.listing.images &&
                  viewModal.listing.images.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold mb-3 text-gray-800">
                        Images
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {viewModal.listing.images.map((image, index) => {
                          const imageUrl =
                            typeof image === "string" ? image : image?.url;
                          const imageSrc = imageUrl
                            ? toApiAbsoluteUrl(imageUrl)
                            : null;
                          return (
                            <div key={index} className="relative">
                              <img
                                src={
                                  imageSrc ||
                                  `https://placehold.co/150x100/png?text=Image+${
                                    index + 1
                                  }`
                                }
                                alt={`Listing ${index + 1}`}
                                className="w-full h-24 object-cover rounded border"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = `https://placehold.co/150x100/png?text=Image+${
                                    index + 1
                                  }`;
                                }}
                              />
                              {imageSrc && (
                                <button
                                  onClick={() =>
                                    window.open(imageSrc, "_blank", "noopener")
                                  }
                                  className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
                                  title="Open full size"
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* Status Information */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-gray-800">
                    Status Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Status
                      </label>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          viewModal.listing.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : viewModal.listing.status === "pending_approval"
                            ? "bg-yellow-100 text-yellow-800"
                            : viewModal.listing.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {viewModal.listing.status || "Unknown"}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Created At
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.createdAt
                          ? new Date(
                              viewModal.listing.createdAt
                            ).toLocaleString()
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Updated At
                      </label>
                      <p className="text-gray-900">
                        {viewModal.listing.updatedAt
                          ? new Date(
                              viewModal.listing.updatedAt
                            ).toLocaleString()
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <button
                    onClick={closeViewModal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeViewModal();
                      openEditModal(viewModal.listing);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    Edit Listing
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Listing</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₹)
                  </label>
                  <input
                    type="number"
                    value={editForm.price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, price: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    rows={3}
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={editForm.phoneNumber}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phoneNumber: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Radius (km)
                  </label>
                  <input
                    type="number"
                    value={editForm.maxRadiusKm}
                    onChange={(e) =>
                      setEditForm({ ...editForm, maxRadiusKm: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={editForm.address}
                    onChange={(e) =>
                      setEditForm({ ...editForm, address: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editForm.tags.join(", ")}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        tags: e.target.value
                          .split(",")
                          .map((tag) => tag.trim())
                          .filter(Boolean),
                      })
                    }
                    className="w-full border p-2 rounded-md"
                    placeholder="e.g., urgent, premium, eco-friendly"
                    disabled={editLoading}
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors ${
                    editLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={editLoading}
                >
                  {editLoading ? "Updating..." : "Update Listing"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
